import { useStore } from '@store/index';
import { buildOrgRoutePath, buildRoutePath } from '@utils/routes';
import { LayoutGrid } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Switch } from '@/components/ui/switch';

interface OrgWideCasesToggleProps {
  className?: string;
}

/**
 * User-controlled opt-in for the Org-wide Cases — the org-wide cases list
 * (spanning every program) plus the new PQC quick-filter tabs.
 *
 * The preference is persisted locally (Zustand persist) and folded into the
 * effective tier by `useUserTier`, so flipping it reroutes the whole Cases
 * experience. We also navigate to the matching cases route immediately so the
 * change is visible at once and the user is never stranded on a route that no
 * longer applies to their effective tier. No page reload is needed — unlike
 * test mode, this doesn't change the API environment.
 *
 * Render this only for eligible users (permission-tier2); it is a no-op for
 * everyone else. See {@link AuthSidebar}.
 */
export const OrgWideCasesToggle: React.FC<OrgWideCasesToggleProps> = ({ className = '' }) => {
  const orgWideCasesEnabled = useStore(state => state.orgWideCasesEnabled);
  const setOrgWideCasesEnabled = useStore(state => state.setOrgWideCasesEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const navigate = useNavigate();

  const handleToggle = (enabled: boolean) => {
    setOrgWideCasesEnabled(enabled);

    if (enabled) {
      toast.success('Org-wide cases enabled — showing cases across all programs');
      if (selectedOrganizationId) {
        navigate(buildOrgRoutePath('/cases', selectedOrganizationId));
      }
    } else {
      toast.success('Switched back to the per-program cases view');
      if (selectedOrganizationId && selectedProgramId) {
        navigate(buildRoutePath('/cases', selectedOrganizationId, selectedProgramId));
      }
    }
  };

  return (
    <div className={`flex items-center justify-between gap-3 whitespace-nowrap ${className}`}>
      <div className="flex items-center gap-2">
        <LayoutGrid className="h-4 w-4 shrink-0" />
        <label htmlFor="org-wide-cases-toggle" className="text-sm font-medium cursor-pointer">
          Org-wide cases
        </label>
      </div>
      <Switch
        id="org-wide-cases-toggle"
        checked={orgWideCasesEnabled}
        onCheckedChange={handleToggle}
        aria-label={orgWideCasesEnabled ? 'Disable org-wide cases' : 'Enable org-wide cases'}
      />
    </div>
  );
};
